import { ReactNode } from "react";
import { Box } from "@mantine/core";
import { OnboardingMantineRoot } from "./OnboardingMantineRoot";
import { RegistrationTracker } from "./RegistrationTracker";
import {
  OnboardingFooter,
  OnboardingHeader,
  REGISTRATION_STEPS,
} from "./onboardingChrome";

// ACA-33/35: shared "card on a stage" shell for the onboarding card screens
// (Profile + Intents), per the Claude Design. NOT a modal dialog — a centered
// presentational card on a full-page light background, with a top bar (logo ·
// signed-in / skip), a progress tracker row, and the shared footer. Screen
// content is passed as children and rendered inside the card.

export interface OnboardingStageProps {
  activeStep: number;
  children: ReactNode;
  signedInAs?: string;
}

export function OnboardingStage({
  activeStep,
  children,
  signedInAs,
}: OnboardingStageProps) {
  return (
    <OnboardingMantineRoot>
      <Box
        data-testid="onboarding-stage"
        style={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          background: "var(--mantine-color-gray-0)",
        }}
      >
        {/* Top bar: logo (left) · signed-in (right) */}
        <OnboardingHeader
          signedInAs={signedInAs}
          signedInTestId="stage-signed-in"
        />

        {/* Progress tracker — own centered row so the connector line renders */}
        <Box style={{ padding: "0.5rem 1.5rem", maxWidth: 760, margin: "0 auto", width: "100%" }}>
          <RegistrationTracker steps={REGISTRATION_STEPS} activeStep={activeStep} />
        </Box>

        {/* Main: card sits at the top, just below the tracker (not vertically centered).
            Extra top padding keeps the card from crowding the tracker (ACA-43). */}
        <Box
          component="main"
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            padding: "3rem 1.5rem 1.5rem",
          }}
        >
          <Box
            data-testid="onboarding-card"
            style={{
              width: "100%",
              maxWidth: 620,
              background: "var(--mantine-color-white)",
              borderRadius: 16,
              boxShadow: "0 18px 48px rgba(20, 20, 20, 0.08), 0 2px 6px rgba(20, 20, 20, 0.04)",
              padding: 40,
            }}
          >
            {children}
          </Box>
        </Box>

        <OnboardingFooter />
      </Box>
    </OnboardingMantineRoot>
  );
}
